import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, MessageSquare, Briefcase, Pin, Lock, Flag, Archive, RefreshCw,
  ExternalLink, Shield, Heart,
} from 'lucide-react';
import { AdminApi, CareerApi } from '../../services/api';
import { QC_PATHS } from '../Community/qoreConnectConfig';
import './QoreTalksAdmin.css';

const Header = ({ icon: Icon, title, subtitle, children }) => (
  <div className="qt-admin__header">
    <div>
      <h2><Icon size={22} /> {title}</h2>
      <p>{subtitle}</p>
    </div>
    {children && <div className="qt-admin__actions">{children}</div>}
  </div>
);

const Stats = ({ items }) => (
  <div className="qt-stats">
    {items.map((s) => (
      <div key={s.label} className="qt-stat">
        <div className="qt-stat__icon" style={{ background: s.color }}>{s.icon}</div>
        <div>
          <p className="qt-stat__value">{s.value}</p>
          <p className="qt-stat__label">{s.label}</p>
        </div>
      </div>
    ))}
  </div>
);

export const QoreConnectCommunityPanel = ({ token, onNotify }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getConnectOverview(token);
      setData(res.success ? res.data : null);
    } catch (e) {
      onNotify?.('error', 'Connect', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, onNotify]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qt-admin">
      <Header icon={Users} title="QoreConnect" subtitle="Members, mentors, and forum health for the community line.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to={QC_PATHS.hub} className="qt-btn qt-btn--primary" target="_blank" rel="noreferrer">
          Open hub <ExternalLink size={14} />
        </Link>
      </Header>
      {loading ? (
        <div className="loading-state"><RefreshCw className="spin" size={24} /></div>
      ) : (
        <Stats items={[
          { icon: <Users size={18} />, value: data?.members || 0, label: 'Members', color: '#22d3ee' },
          { icon: <Heart size={18} />, value: data?.mentors || 0, label: 'Mentors', color: '#34d399' },
          { icon: <Shield size={18} />, value: data?.pendingMentors || 0, label: 'Pending mentors', color: '#f59e0b' },
          { icon: <MessageSquare size={18} />, value: data?.posts || 0, label: 'Live posts', color: '#8b5cf6' },
          { icon: <Flag size={18} />, value: data?.flagged || 0, label: 'Flagged', color: '#fb7185' },
        ]} />
      )}
    </div>
  );
};

export const QoreConnectForumPanel = ({ token, onNotify }) => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await AdminApi.getConnectForum(token, { status, limit: 40 });
      if (res.success) {
        setPosts(res.data.posts || []);
        setStats(res.data.stats || {});
      }
    } catch (e) {
      onNotify?.('error', 'Forum', e.message);
    } finally {
      setLoading(false);
    }
  }, [token, status, onNotify]);

  useEffect(() => { load(); }, [load]);

  const moderate = async (id, action) => {
    setBusy(`${id}:${action}`);
    try {
      const res = await AdminApi.moderateForumPost(token, id, action);
      onNotify?.(res.success ? 'success' : 'error', 'Moderation', res.message || action);
      await load();
    } catch (e) {
      onNotify?.('error', 'Moderation', e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="qt-admin">
      <Header icon={MessageSquare} title="Forum moderation" subtitle="Pin, lock, flag, and archive QoreConnect discussions.">
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="flagged">Flagged</option>
          <option value="archived">Archived</option>
          <option value="deleted">Deleted</option>
        </select>
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </Header>
      <Stats items={[
        { icon: <MessageSquare size={18} />, value: stats.total || 0, label: 'Matching', color: '#6366f1' },
        { icon: <Flag size={18} />, value: stats.flagged || 0, label: 'Flagged', color: '#fb7185' },
        { icon: <Pin size={18} />, value: stats.pinned || 0, label: 'Pinned', color: '#f59e0b' },
        { icon: <Lock size={18} />, value: stats.locked || 0, label: 'Locked', color: '#94a3b8' },
      ]} />
      {loading ? (
        <div className="loading-state"><RefreshCw className="spin" size={24} /></div>
      ) : posts.length === 0 ? (
        <p className="empty-state">No posts in this filter.</p>
      ) : (
        <div className="qt-subscribers-table">
          {posts.map((p) => (
            <div key={p._id} className="qt-sub-row" style={{ alignItems: 'flex-start' }}>
              <div className="qt-sub-avatar">{(p.title || '?').charAt(0).toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <p className="qt-sub-email">{p.title}</p>
                <p className="qt-sub-date">
                  {p.category} · {p.status} · {p.author?.firstName || p.author?.email || 'Author'}
                  {p.isPinned ? ' · pinned' : ''}{p.isLocked ? ' · locked' : ''}
                </p>
              </div>
              <div className="qt-chips" style={{ gap: 6 }}>
                {[
                  { action: p.isPinned ? 'unpin' : 'pin', label: p.isPinned ? 'Unpin' : 'Pin' },
                  { action: p.isLocked ? 'unlock' : 'lock', label: p.isLocked ? 'Unlock' : 'Lock' },
                  { action: 'flag', label: 'Flag' },
                  { action: 'archive', label: 'Archive' },
                  { action: 'restore', label: 'Restore' },
                ].map((b) => (
                  <button
                    key={b.action}
                    type="button"
                    className="qt-btn qt-btn--ghost qt-btn--sm"
                    disabled={busy === `${p._id}:${b.action}`}
                    onClick={() => moderate(p._id, b.action)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const QoreConnectJobsPanel = ({ token, onNotify }) => {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [market, live, recent] = await Promise.all([
        CareerApi.getMarketStats().catch(() => null),
        CareerApi.getRealTimeJobs({ query: '', sources: 'arbeitnow,remoteok,remotive,themuse', limit: 24 }).catch(() => null),
        CareerApi.getRecentJobs({ limit: 24 }).catch(() => null),
      ]);
      setStats(market?.data || market || null);
      const liveList = live?.data || [];
      const recentList = recent?.data || [];
      const list = (liveList.length ? liveList : recentList);
      setJobs(Array.isArray(list) ? list : []);
    } catch (e) {
      onNotify?.('error', 'Jobs', e.message);
    } finally {
      setLoading(false);
    }
  }, [onNotify]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="qt-admin">
      <Header icon={Briefcase} title="Job board" subtitle="Live market listings feeding QoreConnect jobs. Employer posting workflows still sit on the public board.">
        <button type="button" className="qt-btn qt-btn--ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
        <Link to={QC_PATHS.jobs} className="qt-btn qt-btn--primary" target="_blank" rel="noreferrer">
          Public board <ExternalLink size={14} />
        </Link>
      </Header>
      {loading ? (
        <div className="loading-state"><RefreshCw className="spin" size={24} /></div>
      ) : (
        <>
          <Stats items={[
            { icon: <Briefcase size={18} />, value: stats?.totalJobs || jobs.length, label: 'Tracked jobs', color: '#10b981' },
            { icon: <Users size={18} />, value: stats?.companies || '—', label: 'Companies', color: '#6366f1' },
          ]} />
          <div className="qt-subscribers-table">
            {jobs.slice(0, 20).map((job, i) => (
              <div key={job.id || job._id || i} className="qt-sub-row">
                <div className="qt-sub-avatar">{(job.title || job.company || 'J').charAt(0)}</div>
                <div>
                  <p className="qt-sub-email">{job.title || 'Untitled role'}</p>
                  <p className="qt-sub-date">{job.company || job.employer} · {job.location || 'Remote'}</p>
                </div>
              </div>
            ))}
            {jobs.length === 0 && <p className="empty-state">No jobs in the current feed.</p>}
          </div>
        </>
      )}
    </div>
  );
};
